// External imports
import { useLayoutEffect, useMemo } from "react";
import PropTypes from "prop-types";
import { FormattedMessage } from "react-intl";

// Internal imports
import Alert from "components/molecules/Alert/Alert";
import CardNew from "components/molecules/Card/CardNew";
import Message from "pages/shipper/ShipmentTracking/components/Chat/components/Message/Message";

/**
 * This function removes duplicate messages from the chat log.
 * If a message is repeated, it will be displayed once with a count of how many times it was repeated.
 * @param {*} messages the messages to be filtered
 * @returns an array of unique messages
 */
function removeDuplicateMessages(messages) {
  const uniqueMessages = messages.reduce((accumulator, currentMessage) => {
    const existingMessage = accumulator.find(
      (msg) =>
        msg.author_name === currentMessage.author_name &&
        msg.message === currentMessage.message &&
        msg.author_name === "System" &&
        msg.message !== null,
    );

    if (existingMessage) {
      existingMessage.count += 1;
      existingMessage.created =
        currentMessage.created > existingMessage.created ? currentMessage.created : existingMessage.created;
    } else {
      currentMessage.count = 1;
      accumulator.push(currentMessage);
    }

    return accumulator;
  }, []);

  return uniqueMessages;
}

/**
 * A component that displays a chat log. It is used on the shipment dashboard to display comments/updates.
 */
const Messages = function ({ data, shipmentID, provider, shipper, showBookingInstruction, openDeleteMessageDialog }) {
  // Filter messages based on category and remove duplicates
  const messages = useMemo(() => {
    if (data.length === 0) return [];

    // Message types to remove.
    // Other types that exist (that we want to keep are: ["general", "upload", "delivered", "flight"];
    const messageTypesToFilter = ["webhook", "schedule_change", "service_change", "auto_update"];

    // Filter messages
    const filteredMessages = data.filter((message) => !messageTypesToFilter.includes(message.type));

    // Remove duplicate messages
    const uniqueMessages = removeDuplicateMessages(filteredMessages);

    return uniqueMessages;
  }, [data]);

  // Scroll to bottom of chat when messages are set or changed
  useLayoutEffect(() => {
    if (messages.length === 0) return;
    const objChat = document.getElementById("chat_body");
    objChat.scrollTop = objChat.scrollHeight;
  }, [messages]);

  return (
    <CardNew.Body
      id="chat_body"
      className="d-flex flex-column overflow-auto bg-white pl-2 pr-2 pt-0 h-100 mb-2"
      style={{ maxHeight: 500 }}
    >
      {/* Booking instructions */}
      {showBookingInstruction && (
        <div className="w-100 h-100 pt-3">
          <Alert variant="info" className="border border-primary border-color-booking-instruction">
            <div className="font-weight-bold font-16 ">
              <FormattedMessage
                id="components.organisms.chat.bookingInstructionTitle"
                defaultMessage="Booking instruction"
              />
            </div>
            <div className="pt-2">
              <FormattedMessage
                id="components.organisms.chat.bookingInstructionDescription"
                defaultMessage="This is a booking instruction, follow me and thou shall live - booking instructions are entered in markdown via staff UI and therefore can contain {hyperlink} or other {random} {formatting}"
                values={{
                  hyperlink: (
                    <span>
                      <a href="">hyperlinks</a>,
                    </span>
                  ),
                  random: <span className="font-weight-bold text-decoration-underline">random</span>,
                  formatting: <i>formatting.</i>,
                }}
              />
            </div>
          </Alert>
          <div className="pt-2 pb-3 pl-2 pr-2">
            <FormattedMessage
              id="components.organisms.chat.noMessages"
              defaultMessage="This is the update stream for shipment {shipmentID} that is handled by {provider}. Any updates from {shipper} will appear here. Feel free to ask anything to {shipper} by typing a message below and clicking Send."
              values={{ shipmentID, provider, shipper }}
            />
          </div>
        </div>
      )}
      {/* List of messages */}
      {messages.length > 0 ? (
        messages.map((msg, idx) => (
          // We determine if the message is a subsequent message if it is from the same author and on the same day
          // const isSubsequentAuthor = idx > 0 && msg.author_email === messages[idx - 1].author_email;
          // const isSubsequentTimestampSameDay = idx > 0 && moment(msg.timestamp).isSame(messages[idx - 1].timestamp, "day");
          // const isSubsequentMessage = isSubsequentAuthor && isSubsequentTimestampSameDay;

          <Message
            key={msg.tracking_key ?? msg.id}
            message={msg}
            openDeleteMessageDialog={openDeleteMessageDialog}
            isSubsequentMessage={false} // Disable subsequent message combining feature - Requested by Mahle
            isFirstMessage={idx === 0}
          />
        ))
      ) : (
        <div className="mt-4">
          <FormattedMessage
            id="tracking.noCategoryUpdatesFound"
            defaultMessage="No {category} found."
            values={{ category: "comments" }}
          />
        </div>
      )}
    </CardNew.Body>
  );
};

Messages.propTypes = {
  data: PropTypes.oneOfType([PropTypes.object, PropTypes.array]).isRequired,
  shipmentID: PropTypes.string,
  provider: PropTypes.string,
  shipper: PropTypes.string,
  showBookingInstruction: PropTypes.bool,
  openDeleteMessageDialog: PropTypes.func,
};

Messages.defaultProps = {
  shipmentID: "",
  provider: "",
  shipper: "",
  showBookingInstruction: false,
  openDeleteMessageDialog: null,
};

export default Messages;
